import { useCallback, useState } from "react";

interface PageState {
  readonly page: number;
  readonly scope: string;
}

/**
 * A page number tied to the scope of the query it belongs to (the debounced
 * search, the filters). When the scope changes the page reads as 1 on that very
 * render, so the first request for a new scope is already page 1: no effect,
 * and no wasted (audited, throttled) read of the old page number.
 */
export function useScopedPage(scope: string) {
  const [state, setState] = useState<PageState>({ page: 1, scope });
  // Reset during render (the React-sanctioned pattern), so returning to an
  // earlier scope starts on page 1 instead of resurrecting its old page.
  if (state.scope !== scope) setState({ page: 1, scope });
  const page = state.scope === scope ? state.page : 1;
  const setPage = useCallback((next: number) => setState({ page: next, scope }), [scope]);
  return [page, setPage] as const;
}
